/**
 * src/shell/portal/components/DtnSyncIndicator.tsx
 *
 * Slim, non-blocking visual indicator for Offline DTN Outbox.
 * Features:
 * - Ambient status pill indicating queue state
 * - Tap-to-inspect drawer showing queued actions
 * - SYNC_REJECTED inline resolution (Retry / Discard)
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, AlertTriangle, Trash2, RotateCcw, X, WifiOff, Cloud } from 'lucide-react';
import { QueuedWallAction } from '../../../domain/sync/dtn-sync-contracts';
import { dtnSyncManager } from '../../../domain/sync/dtn-sync-manager';

export const DtnSyncIndicator: React.FC = () => {
  const [queue, setQueue] = useState<QueuedWallAction[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const unsub = dtnSyncManager.subscribe(setQueue);
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsub();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const pendingCount = queue.filter((i) => i.status === 'PENDING').length;
  const syncingCount = queue.filter((i) => i.status === 'SYNCING').length;
  const rejectedCount = queue.filter((i) => i.status === 'REJECTED').length;

  if (queue.length === 0 && isOnline) {
    return null; // Silent when idle and online
  }

  return (
    <>
      {/* Top Slim Floating Status Pill */}
      <div className="fixed top-3 left-1/2 -translate-x-1/2 z-50 animate-fade-in font-sans">
        <button
          type="button"
          onClick={() => setIsDrawerOpen(true)}
          className={`px-3.5 py-1.5 rounded-full backdrop-blur-md border text-xs font-semibold flex items-center gap-2 shadow-xl transition hover:scale-105 ${
            rejectedCount > 0
              ? 'bg-amber-950/80 border-amber-500/40 text-amber-200'
              : syncingCount > 0
              ? 'bg-indigo-950/80 border-indigo-500/40 text-indigo-200'
              : !isOnline
              ? 'bg-neutral-900/90 border-neutral-700 text-neutral-300'
              : 'bg-emerald-950/80 border-emerald-500/40 text-emerald-200'
          }`}
        >
          {rejectedCount > 0 ? (
            <>
              <AlertTriangle size={13} className="text-amber-400" />
              <span>{rejectedCount} sync failed · Tap to review</span>
            </>
          ) : syncingCount > 0 ? (
            <>
              <RefreshCw size={13} className="animate-spin text-indigo-400" />
              <span>Syncing {syncingCount} to mesh...</span>
            </>
          ) : !isOnline ? (
            <>
              <WifiOff size={13} className="text-amber-400" />
              <span>Offline ({pendingCount} queued in DTN outbox)</span>
            </>
          ) : (
            <>
              <Cloud size={13} className="text-emerald-400" />
              <span>{pendingCount} queued · Tap to inspect</span>
            </>
          )}
        </button>
      </div>

      {/* Tap-to-Inspect Drawer */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 font-sans animate-fade-in">
          <div className="w-full max-w-md bg-[#0e121c] border border-white/10 rounded-3xl p-5 text-white shadow-2xl">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Cloud size={16} className="text-indigo-400" />
                <h3 className="text-sm font-bold text-white">Sovereign DTN Outbox</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsDrawerOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="text-xs text-gray-400 mb-3">
              Actions stored locally in your sovereign outbox. Drains automatically when mesh connectivity is established.
            </div>

            <div className="max-h-64 overflow-y-auto space-y-2 mb-4 pr-1">
              {queue.length === 0 ? (
                <div className="p-4 rounded-xl bg-white/[0.02] text-center text-gray-500">
                  Outbox is currently empty.
                </div>
              ) : (
                queue.map((item) => (
                  <div
                    key={item.id}
                    className={`p-3 rounded-2xl border transition ${
                      item.status === 'REJECTED'
                        ? 'bg-amber-950/20 border-amber-500/30'
                        : item.status === 'SYNCING'
                        ? 'bg-indigo-950/20 border-indigo-500/30'
                        : 'bg-white/[0.02] border-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-white text-[11px] font-mono">
                        {item.type}
                      </span>
                      <span
                        className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                          item.status === 'REJECTED'
                            ? 'bg-amber-500/20 text-amber-300'
                            : item.status === 'SYNCING'
                            ? 'bg-indigo-500/20 text-indigo-300'
                            : 'bg-gray-800 text-gray-400'
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>

                    {item.payload?.text && (
                      <p className="text-xs text-gray-300 line-clamp-1 mb-1">
                        "{item.payload.text}"
                      </p>
                    )}

                    {item.status === 'REJECTED' && (
                      <div className="mt-2 pt-2 border-t border-amber-500/20 flex items-center justify-between">
                        <span className="text-[10px] text-amber-300">
                          {item.rejectionReason || 'Sync failed'}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => dtnSyncManager.retryItem(item.id)}
                            className="px-2 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[10px] flex items-center gap-1"
                          >
                            <RotateCcw size={10} /> Retry
                          </button>
                          <button
                            type="button"
                            onClick={() => dtnSyncManager.discardItem(item.id)}
                            className="p-1 rounded-lg hover:bg-red-500/20 text-red-400 text-[10px]"
                            title="Discard draft"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="pt-2 flex items-center justify-between border-t border-white/5">
              <span className="text-[11px] text-gray-400 font-mono">
                {isOnline ? '🟢 Connected to Mesh' : '🟡 Offline Mode Active'}
              </span>
              <button
                type="button"
                onClick={() => {
                  dtnSyncManager.drainQueue();
                }}
                className="px-3.5 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-semibold text-gray-300 border border-white/10"
              >
                Sync Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
